import './MessageCard.scss';

import React, {
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';

import { TbDownload } from 'react-icons/tb';

import CustomAvatar from '../../../../../components/CustomAvatar/CustomAvatar';
import CustomReadMoreContent
  from '../../../../../components/CustomReadMoreText/CustomReadMoreContent';
import MentionedContent
  from '../../../../../components/CustomReadMoreText/MentionedContent';
import ProfileTitle from '../../../../../components/ProfileItem/ProfileTitle';
import {
  DEFAULT_AVATAR,
  DELETED_USER,
} from '../../../../../shared/data';
import { downloadFile } from '../../../../../shared/file';
import { useAppNavigate } from '../../../../../shared/hooks';
import { TFileTypes } from '../../../../../types/communityTypes';
import { ROUTES } from '../../../../../types/routes';
import {
  IServiceRequestMessage,
} from '../../../../../types/tqConnectTypes/service-requests';
import CreatePostMediaRender
  from '../../../../CreateCommunityPost/components/CreatePostMediaRender/CreatePostMediaRender';
import MessageActions from './MessageActions';
import MessageDate from './MessageDate';

interface Props {
  serviceRequestId: string;
  message: IServiceRequestMessage;
  hideHeader?: boolean;
  maxVisibleFiles?: number;
}

const MessageCard: React.FC<Props> = ({
  serviceRequestId,
  message: item,
  hideHeader,
  // maxVisibleFiles = 3,
}) => {
  const navigate = useAppNavigate();
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { message, sender, createdAt } = item;
  const {
    _id: senderId,
    name: senderName = DELETED_USER,
    profilePicUrl = DEFAULT_AVATAR,
  } = sender || {};

  const goToUserProfile = (userId?: string) => {
    if (!userId) return;

    navigate(ROUTES.USER_PROFILE, { userId });
  };

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsActionsOpen(true);
  }, []);

  const handleTouchStart = useCallback(() => {
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      setIsActionsOpen(true);
    }, 500);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  return (
    <div
      className="message-card"
      id={`message-card-${item._id}`}
      data-message-id={item._id}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      style={{
        cursor: 'context-menu',
        touchAction: 'manipulation',
      }}>
      {hideHeader ? (
        <span className="message-card__date-hover" style={{ width: 40 }}>
          <MessageDate date={createdAt} />
        </span>
      ) : (
        <CustomAvatar
          src={profilePicUrl}
          size={40}
          border={0}
          className="message-card__avatar"
          onClick={() => goToUserProfile(senderId)}
        />
      )}

      <div className="message-card__content">
        {hideHeader ? null : (
          <div className="message-card__content__header">
            <ProfileTitle
              title={senderName}
              titleStyle={{ fontSize: 14, fontWeight: 600 }}
              onClick={() => goToUserProfile(senderId)}
            />

            <span className="message-card__content__header__extra">
              <MessageDate date={createdAt} />
            </span>
          </div>
        )}

        {message.trim() ? (
          <CustomReadMoreContent
            key={`message-text-${item._id}`}
            className="message-card__content__text"
            lines={5}
            seeMoreText="Read more"
            seeLessText="Read less">
            <MentionedContent content={message || ''} />
          </CustomReadMoreContent>
        ) : null}

        {item?.assets && item.assets?.length > 0 ? (
          <div className="message-card__content__assets">
            {item.assets.map((asset) => (
              <div
                key={asset.asset}
                className="message-card__content__asset-wrapper">
                <CreatePostMediaRender
                  key={asset.asset}
                  files={[
                    {
                      url: asset?.contentUrl,
                      type: asset?.fileType as TFileTypes,
                      name: asset?.fileName,
                      fileSize: asset?.fileSize,
                      isCompressDone: true,
                      _id: asset?.asset,
                    },
                  ]}
                  isMessageRender
                />
                <button
                  type="button"
                  className="message-card__content__asset-download"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(
                      asset?.contentUrl,
                      asset?.fileName || 'download',
                    );
                  }}
                  title="Download"
                  aria-label="Download">
                  <TbDownload />
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <MessageActions
        serviceRequestId={serviceRequestId}
        message={item}
        goToUserProfile={goToUserProfile}
        isOpen={isActionsOpen}
        onClose={() => setIsActionsOpen(false)}
      />
    </div>
  );
};

export default memo(MessageCard);
